import {
	FrontendSample,
	FrontendSampleSpecification,
	FrontendClient,
	FrontendCompanyWithAdditionalInfomation,
	FrontendOrganization,
	FrontendOrganizationLicense,
	FrontendUserInfo,
	FrontendCompanyTestCategory,
	FrontendSampleSpecificationDirectory,
} from "node-library/dist/portal";
import { useEffect, useRef, useState } from "react";
import { CustomTextInput } from "../../common-components/inputs";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import { GreenButton } from "../../common-components/button";
import { ButtonTextLoadingAnimation } from "../../common-components/animation/ButtonLoadingAnimation";
import { Severity, SystemAddOn } from "node-library/dist/constants";
import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import { sendGETRequest } from "../../requestHelper/sendRequest";
import { OrderDetailsAPIExport } from "node-library/dist/apiDataFormats";
import { GreenTickIcon } from "../../common-components/toast/successIcons";
import ClientPortalOrderDetailsContent from "./clientPortalOrderDetailsContent";
import { doesCompanyHaveAddonsEnabled } from "node-library/dist/helpers/request_helper";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;

	user: FrontendUserInfo | undefined;
	setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
	gotoViewOrderPage: () => void;
}

export default function RegisterClientPortalOrderPage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { company, user } = props;

	const [loading, setLoading] = useState<boolean>(false);

	const [clientPortalId, setClientPortalId] = useState<string>("");

	const [orderInfo, setOrderInfo] = useState<OrderDetailsAPIExport>();

	const [associatedOrg, setAssociatedOrg] = useState<FrontendOrganization>();

	const [associatedOrgClients, setAssociatedOrgClients] = useState<
		Array<FrontendClient>
	>([]);

	const [associatedOrgLicenses, setAssociatedOrgLicenses] = useState<
		Array<FrontendOrganizationLicense>
	>([]);

	//register order states
	const [orderSelectedClient, setOrderSelectedClient] =
		useState<FrontendClient>();
	const [orderSelectedLicense, setOrderSelectedLicense] =
		useState<FrontendOrganizationLicense>();

	const [associatedOrgSamples, setAssociatedOrgSamples] = useState<
		Array<FrontendSample>
	>([]);

	const [associatedOrgSpecifications, setAssociatedOrgSpecifications] =
		useState<Array<FrontendSampleSpecification>>([]);

	const [organizationSpecDirectories, setOrganizationSpecDirectories] =
		useState<Array<FrontendSampleSpecificationDirectory>>([]);
	const cannabisTestCategory: FrontendCompanyTestCategory | undefined =
		company.companyTestCategories.find((category) => {
			return category.testCategoryName == "Cannabis";
		});
	//validations
	const [clientPortalIdError, setClientPortalIdError] = useState<string>("");

	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		inputRef.current?.focus();
	}, []);

	async function handleLoadFromClientPortal() {
		if (!cannabisTestCategory) {
			openSnackbar(
				translation("UnableToFindCannabisCategory"),
				Severity.ERROR,
				undefined,
				<RedErrorIcon />,
			);
			return;
		}
		setLoading(true);
		try {
			const response = await sendGETRequest(
				"/cp-register/get-orders-from-client-portal",
				{
					clientPortalId,
					testCategoryId: cannabisTestCategory.testCategoryId,
				},
			);
			const orderInfo: OrderDetailsAPIExport = response.data.orderInfo;
			const associatedOrg: FrontendOrganization = response.data.frontendOrg;
			const associatedOrgClients: Array<FrontendClient> =
				response.data.frontendClients;
			const associatedOrgLicenses: Array<FrontendOrganizationLicense> =
				response.data.frontendClientLicense;
			const frontendSamples: Array<FrontendSample> =
				response.data.frontendSamples;

			const frontendSpecifications: Array<FrontendSampleSpecification> =
				response.data.frontendSpecifications;

			const frontendSpecDirectories: Array<FrontendSampleSpecificationDirectory> =
				response.data.frontendDirectory;

			setOrderInfo(orderInfo);
			setAssociatedOrg(associatedOrg);
			setAssociatedOrgClients(associatedOrgClients);
			setAssociatedOrgLicenses(associatedOrgLicenses);
			setAssociatedOrgSamples(frontendSamples);
			setAssociatedOrgSpecifications(frontendSpecifications);
			setOrganizationSpecDirectories(frontendSpecDirectories);

			openSnackbar(
				translation("OrderDetailsLoaded"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);
		} catch (error: any) {
			console.error(error);

			if (error.response && error.response.status === 404) {
				openSnackbar(
					translation("UnableToIdentifyOrganization"),
					Severity.ERROR,
					translation("UnableToIdentifyOrganizationDescription"),
					<RedErrorIcon />,
				);
			} else if (error.response && error.response.status === 409) {
				openSnackbar(
					translation("OrderHasBeenCancelled"),
					Severity.ERROR,
					translation("OrderHasBeenCancelledDescription"),
					<RedErrorIcon />,
				);
			} else if (error.response.data == "Order already registered!") {
				openSnackbar(
					translation("OrderAlreadyRegistered"),
					Severity.ERROR,
					translation("OrderAlreadyRegisteredDescription"),
					<RedErrorIcon />,
				);
			} else {
				openSnackbar(
					translation("SystemError"),
					Severity.ERROR,
					translation("SystemErrorMsg"),
					<RedErrorIcon />,
				);
			}
		} finally {
			setLoading(false);
		}
	}

	const orderIdInput: React.ReactNode = (
		<CustomTextInput
			ref={inputRef}
			id={`client-portal-id-input`}
			value={clientPortalId}
			setValue={(value) => {
				setClientPortalId(value);
			}}
			label={translation("OrderBarcodeId")}
			required
			errorMsg={clientPortalIdError}
			setErrorMsg={setClientPortalIdError}
			onKeyDown={(e) => {
				if (e.key === "Enter") {
					handleLoadFromClientPortal();
				}
			}}
		/>
	);

	const orderDetailsContent: React.ReactNode =
		orderInfo && associatedOrg ? (
			<ClientPortalOrderDetailsContent
				company={props.company}
				setCompany={props.setCompany}
				user={props.user}
				orderInfo={orderInfo}
				cannabisTestCategory={
					cannabisTestCategory as FrontendCompanyTestCategory
				}
				associatedOrg={associatedOrg}
				setAssociatedOrg={setAssociatedOrg}
				associatedOrgClients={associatedOrgClients}
				orderSelectedClient={orderSelectedClient}
				setOrderSelectedClient={setOrderSelectedClient}
				associatedOrgLicenses={associatedOrgLicenses}
				setAssociatedOrgLicenses={setAssociatedOrgLicenses}
				associatedOrgSamples={associatedOrgSamples}
				setAssociatedOrgSamples={setAssociatedOrgSamples}
				orderSelectedLicense={orderSelectedLicense}
				setOrderSelectedLicense={setOrderSelectedLicense}
				organizationSpecifications={associatedOrgSpecifications}
				setOrganizationSpecifications={setAssociatedOrgSpecifications}
				organizationSpecDirectories={organizationSpecDirectories}
				setUnsavedChanges={props.setUnsavedChanges}
				gotoViewOrderPage={props.gotoViewOrderPage}
			></ClientPortalOrderDetailsContent>
		) : null;

	const pageContent: React.ReactNode = (
		<div>
			<div className="barcode-input-row">
				{orderIdInput}
				<GreenButton
					key="search-barcode-button"
					id="search-barcode-button"
					size="flex"
					children={
						loading ? (
							<ButtonTextLoadingAnimation
								text={translation("Loading")}
							></ButtonTextLoadingAnimation>
						) : (
							translation("Search")
						)
					}
					disabled={loading}
					onClick={() => {
						handleLoadFromClientPortal();
					}}
				></GreenButton>
			</div>
			{orderDetailsContent}
		</div>
	);
	return doesCompanyHaveAddonsEnabled(props.company, [
		SystemAddOn.CLIENT_PORTAL_INTEGRATION,
	]) ? (
		pageContent
	) : (
		<></>
	);
}
